import React, { useState, useEffect, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { getAppTheme } from './theme/tolteckTheme';
import { Toaster } from 'sonner';
import {
  Route,
  Navigate,
  createRoutesFromElements,
  createBrowserRouter,
  RouterProvider
} from 'react-router-dom';
import AppLayout from './components/AppLayout';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import PageTransition from './components/common/PageTransition';
import { AnimatePresence } from 'framer-motion';

// Lazy loading de componentes
const TableComponent = React.lazy(() => import('./components/facturas/Table'));
const FacturasPagadas = React.lazy(() => import('./components/FacturasPagadas'));
const Proveedores = React.lazy(() => import('./components/Proveedores'));
const FdePagos = React.lazy(() => import('./components/FormasDePago'));
const Informes = React.lazy(() => import('./components/Informes'));
const Login = React.lazy(() => import('./components/Login'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword'));
const Usuarios = React.lazy(() => import('./components/Usuarios'));

// Contexts
export const ThemeModeContext = React.createContext({ toggleColorMode: () => {} });

const container = document.getElementById('root');
const root = createRoot(container);

// Componente de loading
const LoadingSpinner = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="50vh"
  >
    <CircularProgress size={60} />
  </Box>
);

// Componente contenedor para el layout común
const Layout = ({ children }) => {
  const [mode, setMode] = React.useState(() => {
    return localStorage.getItem('appMode') || 'light';
  });

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('appMode', newMode);
          return newMode;
        });
      },
    }),
    [],
  );

  const theme = React.useMemo(() => getAppTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Toaster position="bottom-right" closeButton expand={false} theme={mode} />
        <AppLayout>
          <Suspense fallback={<LoadingSpinner />}>
            <AnimatePresence mode="wait">
              {children}
            </AnimatePresence>
          </Suspense>
        </AppLayout>
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route path="/login" element={
        <Suspense fallback={<LoadingSpinner />}>
          <Login />
        </Suspense>
      } />
      <Route path="/reset-password/:token" element={
        <Suspense fallback={<LoadingSpinner />}>
          <ResetPassword />
        </Suspense>
      } />

      {/* Rutas protegidas */}
      <Route path="/" element={
        <PrivateRoute>
          <Layout>
            <PageTransition>
              <TableComponent />
            </PageTransition>
          </Layout>
        </PrivateRoute>
      } />

      <Route path="/facturaspagadas" element={
        <PrivateRoute>
          <Layout>
            <PageTransition>
              <FacturasPagadas />
            </PageTransition>
          </Layout>
        </PrivateRoute>
      } />

      <Route path="/proveedores" element={
        <PrivateRoute>
          <Layout>
            <PageTransition>
              <Proveedores />
            </PageTransition>
          </Layout>
        </PrivateRoute>
      } />

      <Route path="/fdepagos" element={
        <PrivateRoute>
          <Layout>
            <PageTransition>
              <FdePagos />
            </PageTransition>
          </Layout>
        </PrivateRoute>
      } />

      <Route path="/informes" element={
        <PrivateRoute>
          <Layout>
            <PageTransition>
              <Informes />
            </PageTransition>
          </Layout>
        </PrivateRoute>
      } />

      <Route path="/usuarios" element={
        <PrivateRoute adminOnly={true}>
          <Layout>
            <PageTransition>
              <Usuarios />
            </PageTransition>
          </Layout>
        </PrivateRoute>
      } />

      {/* Ruta por defecto - redirigir a la página principal */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  ),
  {
    future: {
      v7_relativeSplatPath: true
    }
  }
);

root.render(
  <RouterProvider router={router} />
);